import { Box, type BoxProps, ScrollArea } from '@mantine/core';
import { ReactNode } from 'react';
import { DataContainer, DataContainerProps } from './DataContainer';

export interface ScrollContainerProps extends Omit<BoxProps, 'children'> {
	/** Header content - always visible and non-scrollable */
	header?: ReactNode;
	/** Main content - scrollable area */
	children: ReactNode;
	/** Footer content - always visible and non-scrollable */
	footer?: ReactNode;
	/** Props for the header container */
	headerProps?: BoxProps;
	/** Props for the content container */
	contentProps?: BoxProps;
	/** Props for the footer container */
	footerProps?: BoxProps;
	/** Controls whether content area is scrollable */
	scrollEnabled?: boolean;
	/** Data handling props (when integrated with DataContainer) */
	dataProps?: Omit<DataContainerProps, 'children'>;
}

/**
 * ScrollContainer provides a flex-based layout with fixed header/footer and scrollable content.
 * Designed to work within layouts that use flex column to control full height.
 *
 * Layout structure:
 * - Header: Fixed at top (flex-shrink: 0)
 * - Content: Grows to fill available space with optional scroll (flex: 1, overflow controlled by scrollEnabled)
 * - Footer: Fixed at bottom (flex-shrink: 0)
 *
 * @example
 * ```tsx
 * <ScrollContainer
 *   header={<PageHeader title="Users" />}
 *   footer={<ActionButtons />}
 *   scrollEnabled={true}
 * >
 *   <UserList />
 * </ScrollContainer>
 * ```
 */
export function ScrollContainer({
	header,
	children,
	footer,
	headerProps,
	contentProps,
	footerProps,
	scrollEnabled = true, // Default to scrollable
	dataProps,
	...boxProps
}: ScrollContainerProps) {
	return (
		<Box
			{...boxProps}
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				...boxProps.style,
			}}
		>
			{/* Header - Fixed at top */}
			{!dataProps?.loading && !dataProps?.error && header && (
				<Box
					px="md"
					pt="md"
					{...headerProps}
					style={{
						flexShrink: 0,
						...headerProps?.style,
					}}
				>
					{header}
				</Box>
			)}

			{/* Content - Scrollable area */}
			{scrollEnabled ? (
				<ScrollArea
					style={{
						flex: 1,
					}}
				>
					<Box px="md" pt="md" pb="xl" {...contentProps}>
						{dataProps ? (
							<DataContainer {...dataProps}>{children}</DataContainer>
						) : (
							children
						)}
					</Box>
				</ScrollArea>
			) : (
				<Box
					{...contentProps}
					style={{
						flex: 1,
						minHeight: 0,
						overflow: 'hidden',
						...contentProps?.style,
					}}
				>
					{dataProps ? (
						<DataContainer {...dataProps}>{children}</DataContainer>
					) : (
						children
					)}
				</Box>
			)}

			{/* Footer - Fixed at bottom */}
			{!dataProps?.loading && !dataProps?.error && footer && (
				<Box
					{...footerProps}
					style={{
						flexShrink: 0,
						...footerProps?.style,
					}}
				>
					{footer}
				</Box>
			)}
		</Box>
	);
}
